/**
 * global function to be used site-wide.
 */

/**
 * Returns a debounced version of the provided function.
 * The debounced function delays invoking `func` until after `wait` milliseconds have
 * elapsed since the last time it was invoked. If `immediate` is true, the function is
 * triggered on the leading edge instead.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @param {boolean} [immediate=false] - If true, trigger on the leading edge.
 * @returns {Function} - A debounced function.
 */
export const debounce = (func, wait, immediate = false) => {
    let timeout = null;

    return function (...args) {
        const context = this;
        const callNow = immediate && timeout === null;

        clearTimeout(timeout);

        timeout = setTimeout(() => {
            timeout = null;

            if (!immediate) {
                func.apply(context, args);
            }
        }, wait);

        if (callNow) {
            func.apply(context, args);
        }
    };
};

/**
 * Returns a throttled version of the provided function.
 * The throttled function ensures that `func` is called at most once every `delay` milliseconds.
 * Useful for performance optimization during frequent events like scroll or resize.
 *
 * @param {Function} func - The function to throttle.
 * @param {number} delay - The minimum delay (in ms) between calls.
 * @returns {Function} - A throttled function.
 */
export const throttle = (func, delay) => {
    let timeout = null;

    return function (...args) {
        if (timeout) return;

        func.apply(this, args);

        timeout = setTimeout(() => {
            timeout = null;
        }, delay);
    };
};

/**
 * Get the current page's language from the <html> tag. 
 * Only returns the first part (e.g., "da" from "da-DK"): 
 * @returns {string} The primary language code, defaults to 'en'.
 */
export const pageLanguage = () => {
    const lang = document.documentElement.lang || 'en';
    return lang.split('-')[0].toLowerCase();
};

/**
 * Check if the user prefers reduced motion via OS settings.
 * @returns {boolean} True if reduced motion is preferred.
 */
export const reduceMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Check if the current device is mobile-sized (max-width: 1199px).
 * @returns {boolean} True if viewport is considered mobile.
 */
export const isMobile = () =>
    window.matchMedia('(max-width: 1199px)').matches;

/**
 * Toggle the 'scrolled-down' class on <body> if scrolled past a threshold.
 * @param {number} [px=100] - The scroll distance from the top to trigger the class.
 */
export const onScrolledDown = (px = 100) => {
    const scrollPosition =
        window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.classList.toggle('scrolled-down', scrollPosition > px);
};

/**
 * Add scroll direction detection by toggling 'going-up' and 'going-down' classes on <body>.
 * Sets up a scroll listener.
 */
export const scrollDirection = () => {
    // Store the previous scroll position in order to detect direction.
    let lastScrollY = window.scrollY;

    /**
     * Updates the body's scroll direction classes.
     */
    const updateDirection = () => {
        // Clamp to 0 to avoid negative values during iOS overscroll.
        const currentScrollY = Math.max(0, window.scrollY);

        // Ignore events where the scroll position hasn't changed.
        if (currentScrollY === lastScrollY) {
            return;
        }

        const goingDown = currentScrollY > lastScrollY;

        // Keep only the class representing the current scroll direction.
        document.body.classList.toggle('going-down', goingDown);
        document.body.classList.toggle('going-up', !goingDown);

        // Save the current position for the next scroll event.
        lastScrollY = currentScrollY;
    };

    // Listen for scroll events without blocking browser scrolling.
    window.addEventListener('scroll', updateDirection, { passive: true });
};

/**
 * Smoothly scroll to top and optionally focus the main page content.
 * Requires an element with id="scrollTop" and optionally id="page".
 */
export const scrollTop = () => {
    const btn = document.getElementById('scrollTop');
    const skip = document.getElementById('page');

    if (btn) {
        btn.addEventListener('click', () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            skip?.focus();
        });
    }
};